import * as select from '@zag-js/select';
import { omit } from '@zag-js/utils';
import { useMachine, normalizeProps } from '@zag-js/vue';
import { useId, computed, watch } from 'vue';
import { useEnvironmentContext } from '../../providers/environment/use-environment-context.js';
import { useLocaleContext, DEFAULT_LOCALE } from '../../providers/locale/use-locale-context.js';
import { cleanProps } from '../../utils/clean-props.js';
import { useFieldContext } from '../field/use-field-context.js';

const useSelect = (props, emit) => {
  const id = useId();
  const env = useEnvironmentContext();
  const locale = useLocaleContext(DEFAULT_LOCALE);
  const field = useFieldContext();
  const initialContext = computed(() => ({
    id,
    ids: {
      label: field?.value.ids.label,
      hiddenSelect: field?.value.ids.control
    },
    disabled: field?.value.disabled,
    readOnly: field?.value.readOnly,
    invalid: field?.value.invalid,
    required: field?.value.required,
    dir: locale.value.dir,
    open: props.defaultOpen,
    "open.controlled": props.open !== void 0,
    value: props.modelValue ?? props.defaultValue,
    getRootNode: env?.value.getRootNode,
    onValueChange: (details) => {
      emit?.("valueChange", details);
      emit?.("update:modelValue", details.value);
    },
    onHighlightChange: (details) => emit?.("highlightChange", details),
    onOpenChange: (details) => {
      emit?.("openChange", details);
      emit?.("update:open", details.open);
    },
    onFocusOutside: (details) => emit?.("focusOutside", details),
    onInteractOutside: (details) => emit?.("interactOutside", details),
    onPointerDownOutside: (details) => emit?.("pointerDownOutside", details),
    ...cleanProps(props)
  }));
  const [state, send, service] = useMachine(select.machine(initialContext.value), {
    context: computed(() => omit(initialContext.value, ["collection"]))
  });
  watch(
    () => props.collection,
    (collection) => {
      service.setContext({ collection });
    }
  );
  return computed(() => select.connect(state.value, send, normalizeProps));
};

export { useSelect };
